import { cn } from '@nesso/ui/lib/utils';
import { Badge } from '@nesso/ui/primitives/badge';
import { Button } from '@nesso/ui/primitives/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@nesso/ui/primitives/card';

import type { SubscriptionMe } from '@/entities/subscription';

import { formatBillingStatusLine } from '../../model/format-billing-status-line';
import type { BillingStatus } from '../../model/get-billing-status';
import { getBillingStatusBadge } from '../../model/get-billing-status-badge';
import { getCardExpiryWarning } from '../../model/get-card-expiry-warning';
import { PlanBillingSummaryFooter } from './plan-billing-summary-card/plan-billing-summary-footer';
import { PlanBillingSummaryPaymentMethod } from './plan-billing-summary-card/plan-billing-summary-payment-method';

type Props = {
    status: BillingStatus;
    subscription: SubscriptionMe;
    // Neither is on the subscription: the incoming plan's size comes from the plan catalog, and
    // reactivation progress from the mutation running it.
    scheduledPlanSessions: number | 'unlimited' | null;
    isReactivating: boolean;
    onInvoiceHistory: () => void;
    onEditPaymentMethod: () => void;
    onManagePlan: () => void;
    onReactivate: () => void;
    onChoosePlan: () => void;
};

export function PlanBillingSummaryCard({
    status,
    subscription,
    scheduledPlanSessions,
    isReactivating,
    onInvoiceHistory,
    onEditPaymentMethod,
    onManagePlan,
    onReactivate,
    onChoosePlan,
}: Props) {
    const badge = getBillingStatusBadge(status);
    const line = formatBillingStatusLine(status, subscription);
    const expiryWarning = getCardExpiryWarning(status, subscription);

    return (
        <Card className="mb-8 pb-0">
            <CardHeader>
                {/* Stacked, the badge takes the header's first row so the title shares the
                    second with the button, as the mobile design draws it. */}
                <div
                    className={cn(
                        'flex items-center gap-1.5',
                        badge && '@max-[479px]/card-header:contents',
                    )}
                >
                    <CardTitle className="text-base font-bold @max-[479px]/card-header:row-start-2">
                        Billing summary
                    </CardTitle>
                    {badge && (
                        <Badge
                            variant={badge.variant}
                            className="@max-[479px]/card-header:row-start-1 @max-[479px]/card-header:justify-self-start"
                        >
                            {badge.label}
                        </Badge>
                    )}
                </div>
                <CardAction className={cn(badge && '@max-[479px]/card-header:row-start-2')}>
                    <Button variant="outline" size="sm" onClick={onInvoiceHistory}>
                        Invoice History
                    </Button>
                </CardAction>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
                {line && <p className="text-base">{line}</p>}
                {subscription.cardLast4 && (
                    <PlanBillingSummaryPaymentMethod
                        brand={subscription.cardBrand}
                        last4={subscription.cardLast4}
                        expMonth={subscription.cardExpMonth}
                        expYear={subscription.cardExpYear}
                        expiryWarning={expiryWarning}
                        onEdit={onEditPaymentMethod}
                    />
                )}
            </CardContent>
            <PlanBillingSummaryFooter
                status={status}
                subscription={subscription}
                scheduledPlanSessions={scheduledPlanSessions}
                isReactivating={isReactivating}
                onManagePlan={onManagePlan}
                onReactivate={onReactivate}
                onChoosePlan={onChoosePlan}
                onUpdateCard={onEditPaymentMethod}
            />
        </Card>
    );
}
